import { Consumer, Subscription } from '@rails/actioncable';

import connection from '~services/connectionModule';

// Store handlers for each channel
const channelHandlers = new Map<string, Map<string, (data: unknown) => void>>();

// ActionCable re-subscribes on reconnect, so these fire on every connect.
const channelConnectedHandlers = new Map<string, Map<string, () => void>>();

const channelDisconnectedHandlers = new Map<string, Map<string, () => void>>();

// Store single subscription per channel
const channelSubscriptions = new Map<string, Subscription<Consumer>>();

// Entity-scoped channels (e.g. JobLoadActivityChannel, which streams per job_id) must not
// share one subscription across different entities, so createOptions participate in the key.
const makeChannelKey = (
  channel: string,
  companyId: string | null,
  createOptions?: Record<string, unknown>,
) => {
  const baseKey = `${channel}_${companyId || 'no-company'}`;

  if (!createOptions) {
    return baseKey;
  }

  const scope = Object.keys(createOptions)
    .sort()
    .map((option) => `${option}:${String(createOptions[option])}`)
    .join('_');

  return scope ? `${baseKey}_${scope}` : baseKey;
};

export const isRealTimeConnectionOpen = () =>
  connection.realTimeConnection.connection.isOpen();

/**
 * Subscribe to a channel with support for multiple handlers.
 * Each component can register its own handler using a unique key.
 */
export const subscribeWithMultipleHandlers = <T>(
  channel: string,
  companyId: string | null,
  uniqueKey: string,
  receivedHandler: (resp: T) => void,
  createOptions?: Record<string, unknown>,
  connectedHandler?: () => void,
  disconnectedHandler?: () => void,
): Subscription<Consumer> | undefined => {
  const consumer = connection.realTimeConnection;
  const channelKey = makeChannelKey(channel, companyId, createOptions);

  // Get or create handler map for this channel
  if (!channelHandlers.has(channelKey)) {
    channelHandlers.set(channelKey, new Map());
  }
  const handlers = channelHandlers.get(channelKey)!;

  if (!channelConnectedHandlers.has(channelKey)) {
    channelConnectedHandlers.set(channelKey, new Map());
  }
  const connectedHandlers = channelConnectedHandlers.get(channelKey)!;

  if (!channelDisconnectedHandlers.has(channelKey)) {
    channelDisconnectedHandlers.set(channelKey, new Map());
  }
  const disconnectedHandlers = channelDisconnectedHandlers.get(channelKey)!;

  // Register this component's handler
  handlers.set(uniqueKey, receivedHandler as (data: unknown) => void);

  if (connectedHandler) {
    connectedHandlers.set(uniqueKey, connectedHandler);
  }

  if (disconnectedHandler) {
    disconnectedHandlers.set(uniqueKey, disconnectedHandler);
  }

  // Check if subscription already exists
  let subscription = channelSubscriptions.get(channelKey);

  // Create subscription if it doesn't exist
  if (!subscription && consumer) {
    subscription = consumer.subscriptions.create(
      {
        channel,
        ...(companyId ? { company_id: companyId } : {}),
        ...createOptions,
      },
      {
        connected: () => {
          connectedHandlers.forEach((handler, key) => {
            try {
              handler();
            } catch (error) {
              console.error(
                `[RTU] Error in connected handler ${key} for ${channel}:`,
                error,
              );
            }
          });
        },
        disconnected: () => {
          disconnectedHandlers.forEach((handler, key) => {
            try {
              handler();
            } catch (error) {
              console.error(
                `[RTU] Error in disconnected handler ${key} for ${channel}:`,
                error,
              );
            }
          });
        },
        received: (data: T) => {
          // Call all registered handlers for this channel
          handlers.forEach((handler, key) => {
            try {
              handler(data);
            } catch (error) {
              console.error(`[RTU] Error in handler ${key} for ${channel}:`, error);
            }
          });
        },
      },
    );

    channelSubscriptions.set(channelKey, subscription);
  }

  return subscription;
};

/**
 * Unsubscribe a specific handler from a channel.
 * If no handlers remain, unsubscribe from the channel entirely.
 * When the subscription was created with createOptions, the same createOptions
 * must be provided here so the matching subscription is targeted.
 */
export const unsubscribeHandler = (
  channel: string,
  companyId: string | null,
  uniqueKey: string,
  createOptions?: Record<string, unknown>,
): void => {
  const channelKey = makeChannelKey(channel, companyId, createOptions);
  const handlers = channelHandlers.get(channelKey);

  channelConnectedHandlers.get(channelKey)?.delete(uniqueKey);
  channelDisconnectedHandlers.get(channelKey)?.delete(uniqueKey);

  if (handlers) {
    // Remove this handler
    handlers.delete(uniqueKey);

    // If no handlers remain, clean up the subscription
    if (handlers.size === 0) {
      channelHandlers.delete(channelKey);
      channelConnectedHandlers.delete(channelKey);
      channelDisconnectedHandlers.delete(channelKey);

      const subscription = channelSubscriptions.get(channelKey);
      if (subscription) {
        subscription.unsubscribe();
        channelSubscriptions.delete(channelKey);
      }
    }
  }
};
